/**
 * /agents/runs — run history across all agents. Rows keep polling while any
 * run is queued/running. Click a row for live progress + logs.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import {
  Box,
  Heading,
  HStack,
  Text,
  Button,
  Spinner,
  Center,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiRefreshCw } from 'react-icons/fi';
import { callAgents } from '../../hooks/useAgents';
import AgentStatusChip from '../../components/Agents/AgentStatusChip';

const fmtDate = (s) => (s ? new Date(s).toLocaleString() : '—');

export default function AgentRunsPage() {
  const [runs, setRuns] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const history = useHistory();
  const { url } = useRouteMatch();
  const sectionUrl = url.replace(/\/runs$/, '');

  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const subtle = useColorModeValue('gray.600', 'gray.400');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await callAgents('listRuns', { limit: 100 });
      setRuns(res.runs || []);
      setTotal(res.total || 0);
    } catch (err) {
      toast({ title: 'Runs load failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  // Keep refreshing while anything is active.
  useEffect(() => {
    const hasActive = runs.some((r) => ['queued', 'running'].includes(r.status));
    if (!hasActive) return undefined;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [runs, load]);

  const progressText = (r) =>
    r.progress?.total
      ? `${r.progress.done || 0}/${r.progress.total}${r.progress.phase ? ` · ${r.progress.phase}` : ''}`
      : r.progress?.phase || '—';

  return (
    <Box p={6}>
      <HStack justify="space-between" mb={4} flexWrap="wrap" spacing={3}>
        <Box>
          <Heading size="md">Runs</Heading>
          <Text fontSize="xs" color={subtle}>{total} run{total === 1 ? '' : 's'}</Text>
        </Box>
        <Button size="sm" leftIcon={<FiRefreshCw />} variant="outline" onClick={load} isLoading={loading}>Refresh</Button>
      </HStack>

      <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" overflow="hidden">
        {loading && !runs.length ? (
          <Center py={16}><Spinner color="blue.400" /></Center>
        ) : (
          <Table size="sm">
            <Thead>
              <Tr><Th>Agent</Th><Th>Status</Th><Th>Trigger</Th><Th>Progress</Th><Th>Started</Th><Th>Finished</Th></Tr>
            </Thead>
            <Tbody>
              {runs.map((r) => (
                <Tr key={r.id} _hover={{ bg: cardBorder, cursor: 'pointer' }} onClick={() => history.push(`${sectionUrl}/runs/${r.id}`)}>
                  <Td fontSize="xs" fontWeight="medium">{r.agent_type}</Td>
                  <Td><AgentStatusChip value={r.status} /></Td>
                  <Td fontSize="xs">{r.trigger_source}</Td>
                  <Td fontSize="xs">{progressText(r)}</Td>
                  <Td fontSize="xs">{fmtDate(r.started_at || r.created_at)}</Td>
                  <Td fontSize="xs">{fmtDate(r.finished_at)}</Td>
                </Tr>
              ))}
              {!runs.length && (
                <Tr><Td colSpan={6}><Center py={10}><Text fontSize="sm" color={subtle}>No runs yet.</Text></Center></Td></Tr>
              )}
            </Tbody>
          </Table>
        )}
      </Box>
    </Box>
  );
}
